import { useCallback, useMemo, useState } from "react";
import { message } from "antd";
import { useStopwatch } from "core/providers/StopwatchContext";
import { useProjectSelection } from "core/providers/ProjectSelectionContext";
import { useCurrentUser } from "features/collaborator/hooks/use-current-user";
import { useProjectAccumulatedTime } from "../hooks/useProjectAccumulatedTime";
import { useSessionSave } from "../hooks/useSessionSave";
import { useStopwatchAvailability } from "../hooks/useStopwatchAvailability";
import { SelectedItem } from "features/architecture/projects/model/types/ProjectSelection";
import { useWorkSessionCategories } from "../hooks/useWorkSessionCategories";
import { createProjectSelectionAdapter } from "features/architecture/projects/model/adapters/createProjectSelectionAdapter";
import { formatDateUI } from "shared/utils/date";

type Params = {
  onStart?: () => void;
};

const getSelectedItemId = (item?: SelectedItem | null): number | undefined => {
  if (item == null) return undefined;
  if (typeof item === "string" || typeof item === "number") return item;

  return item.id;
};

const getPrimaryActionLabel = ({
  isRunning,
  hasPausedSession,
}: {
  isRunning: boolean;
  hasPausedSession: boolean;
}) => {
  if (isRunning) {
    return "Pause";
  }

  if (hasPausedSession) {
    return "Resume";
  }

  return "Start";
};

export function useStopwatchController({ onStart }: Params) {
  const {
    seconds,
    isRunning,
    projectAccumulatedSeconds,
    setProjectAccumulatedSeconds,
    start,
    pause,
    reset,
    hasPausedSession,
    isPreviousDaySession,
    stopwatchWarning,
    sessionDateKey,
  } = useStopwatch();

  const { projectState, setProjectState } = useProjectSelection();
  const user = useCurrentUser();

  const [isConfirmingPreviousDay, setIsConfirmingPreviousDay] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");

  const collaboratorId = user?.collaborator_profile?.id;
  // Availability is a modal-gating hint only; save-time validation still stays in the backend.
  const {
    status: stopwatchAvailabilityStatus,
    warning: stopwatchAvailabilityWarning,
    loading: stopwatchAvailabilityLoading,
  } = useStopwatchAvailability(collaboratorId);

  const projectId = projectState.project?.id;
  const phaseId = getSelectedItemId(projectState.phase?.[0]);
  const blockId = getSelectedItemId(projectState.block?.[0]);
  const stageId = getSelectedItemId(projectState.stage?.[0]);
  const categoryId = projectState.category;

  const canStart =
    !!projectId && !!phaseId && !!blockId && !!stageId && !!categoryId;

  useProjectAccumulatedTime({
    collaboratorId,
    projectId,
    phaseId,
    blockId,
    stageId,
  });

  const { executeSave, hasSessionToSave } = useSessionSave({
    collaboratorId,
    projectId,
    phaseId,
    blockId,
    stageId,
    categoryId,
    setProjectAccumulatedSeconds,
  });

  const { options: categoryOptions, loading: categoriesLoading } =
    useWorkSessionCategories();

  const filteredCategoryOptions = useMemo(() => {
    if (!categorySearch.trim()) return categoryOptions;

    const s = categorySearch.toLowerCase();

    return categoryOptions.filter((o) => o.label.toLowerCase().includes(s));
  }, [categoryOptions, categorySearch]);

  const projectSelection = useMemo(
    () =>
      createProjectSelectionAdapter({
        state: projectState,
        setState: setProjectState,
      }),
    [projectState, setProjectState],
  );

  const warning =
    stopwatchAvailabilityWarning ||
    stopwatchWarning ||
    (hasPausedSession && !canStart
      ? "Please complete the project selection before resuming or saving the session."
      : null);

  const formattedSessionDate = useMemo(() => {
    if (!sessionDateKey) return null;
    return formatDateUI(sessionDateKey);
  }, [sessionDateKey]);

  const validateSelection = useCallback(() => {
    if (!canStart) {
      message.error("Select project, phase, block, stage and category");
      return false;
    }
    return true;
  }, [canStart]);

  const startStopwatch = useCallback(() => {
    if (!validateSelection()) return;

    // Do not start when the backend already says this collaborator is saturated.
    if (stopwatchAvailabilityLoading) {
      message.info("Checking stopwatch availability...");
      return;
    }

    if (stopwatchAvailabilityStatus !== "available") {
      if (stopwatchAvailabilityWarning) {
        message.warning(stopwatchAvailabilityWarning);
      }
      return;
    }

    const res = start();

    if (!res.ok) {
      if (res.reason === "daily_cap_reached") {
        message.warning("Limit reached for today");
        return;
      }

      if (res.reason === "previous_day_session") {
        message.error(
          "This paused session belongs to a previous day. Please Save or Reset it.",
        );
        return;
      }

      return;
    }

    onStart?.();
  }, [
    onStart,
    start,
    validateSelection,
    stopwatchAvailabilityLoading,
    stopwatchAvailabilityStatus,
    stopwatchAvailabilityWarning,
  ]);

  const saveSession = useCallback(async () => {
    if (!validateSelection()) return;

    if (!hasSessionToSave()) return;

    if (isPreviousDaySession) {
      setIsConfirmingPreviousDay(true);
      return;
    }

    await executeSave();
  }, [executeSave, hasSessionToSave, isPreviousDaySession, validateSelection]);

  const resetStopwatch = useCallback(() => {
    if (isRunning) return;
    reset();
  }, [isRunning, reset]);

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmingPreviousDay(false);
  }, []);

  const handleSavePreviousDay = useCallback(async () => {
    setIsConfirmingPreviousDay(false);
    await executeSave();
  }, [executeSave]);

  const handleResetPreviousDay = useCallback(() => {
    setIsConfirmingPreviousDay(false);
    reset();
  }, [reset]);

  const primaryAction = useMemo(() => {
    const label = getPrimaryActionLabel({ isRunning, hasPausedSession });

    if (isRunning) {
      return {
        label,
        onClick: pause,
        disabled: false,
      };
    }

    if (hasPausedSession) {
      // Resume obeys the same modal availability gate as first start.
      return {
        label,
        onClick: startStopwatch,
        disabled:
          isPreviousDaySession ||
          !canStart ||
          stopwatchAvailabilityLoading ||
          stopwatchAvailabilityStatus !== "available",
      };
    }

    // Initial start is blocked while availability is being checked or while the collaborator is saturated.
    return {
      label,
      onClick: startStopwatch,
      disabled:
        !canStart ||
        stopwatchAvailabilityLoading ||
        stopwatchAvailabilityStatus !== "available",
    };
  }, [
    isRunning,
    hasPausedSession,
    pause,
    startStopwatch,
    canStart,
    isPreviousDaySession,
    stopwatchAvailabilityLoading,
    stopwatchAvailabilityStatus,
  ]);

  return {
    seconds,
    projectAccumulatedSeconds,
    isRunning,
    hasPausedSession,
    isPreviousDaySession,
    stopwatchWarning,
    canStart,
    categoryOptions,
    categoriesLoading,
    categorySearch,
    setCategorySearch,
    filteredCategoryOptions,
    projectSelection,
    primaryAction,
    warning,
    formattedSessionDate,
    stopwatchAvailabilityStatus,
    stopwatchAvailabilityLoading,
    isConfirmingPreviousDay,
    startStopwatch,
    saveSession,
    resetStopwatch,
    handleCloseConfirm,
    handleSavePreviousDay,
    handleResetPreviousDay,
    pauseStopwatch: pause,
    executeSave,
  };
}
